import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import {
  AgentRemoteCommand,
  AgentRemoteCommandType,
  BulkRemoteCommand,
  BulkRemoteCommandType,
  GroupRemoteCommand,
  RemoteCommandType
} from 'mbs-ui-shared@models/rm/remote-command-model';
import {
  HttpClientMethodOptions,
  Params,
  RunAsyncCommandType
} from 'mbs-ui-shared@services/async-remote-command/async-remote-command.types';
import { BehaviorSubject, catchError, concatMap, map, Observable, of, Subject, throwError, timer } from 'rxjs';
import { filter, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import { AsyncAgentMinVersion } from './async-remote-command.constants';

interface IntermediateAsyncResult {
  id: number;
  request: Record<string, unknown>;
  response: Record<string, unknown>;
  totalComputers: number;
}

export const timeoutMilliseconds = 10000; // 10 second for backend processing

export const getRemoteCommandUrl = (hid: string, commandType: string): string => `api/computers/${hid}/remotecommand/${commandType}`;

@Injectable({
  providedIn: 'root'
})
export class AsyncRemoteCommandService {
  private http = inject(HttpClient);

  private readonly interval = 10000; // Every 10 seconds
  private readonly timeout = 15 * 60 * 1000; // No longer than 15 minutes
  private $intermediateResult: BehaviorSubject<IntermediateAsyncResult> = new BehaviorSubject(null);

  /**
   * Only runBulkAsyncCommand and runGroupAsyncCommand will return result
   * @return {Observable<IntermediateAsyncResult>}
   */
  get lastAsyncResult(): Observable<IntermediateAsyncResult> {
    return this.$intermediateResult.asObservable();
  }

  runBulkAsyncCommand<TParams extends BulkRemoteCommandType, TResult>({
    url,
    requestIdUrl,
    params
  }: {
    url: 'api/computers/bulkremotecommand';
    requestIdUrl: 'api/computers/groupremotecommand';
    params: BulkRemoteCommand<TParams>;
  }): Observable<TResult> {
    const result = new BulkAsyncProcess<TParams, TResult>(url, requestIdUrl, params);

    return this.runAsync(result);
  }

  runGroupAsyncCommand<TCommands extends AgentRemoteCommand<AgentRemoteCommandType>, TResult>({
    url,
    params
  }: {
    url: 'api/computers/groupremotecommand';
    params: GroupRemoteCommand<TCommands>;
  }): Observable<TResult> {
    const result = new GroupAsyncProcess<TCommands, TResult>(url, params);

    return this.runAsync(result);
  }

  /**
   * Implements Modern async command scenario. Actual for agents 8.0.0 and higher
   * @param {String} hid - computer hid
   * @param {Record<string, unknown>} params - request params
   * @param {Record<string, unknown>} options - request headers, etc.
   * @return {Observable}
   */
  runAsyncCommand({
    hid,
    params,
    options = {
      headers: { 'Content-Type': 'application/json; charset=utf-8' }
    }
  }: RunAsyncCommandType): Observable<any> {
    const result = new AsyncProcessSimple<any>(hid, params, options);

    return this.runAsync<any>(result);
  }

  /**
   * Implements Legacy async command scenario. Actual for agents < 8.0.0
   * @param {String} hid - computer hid
   * @param {Record<string, unknown>} params - request params
   * @param {Record<string, unknown>} options - request headers, etc.
   * @return {Observable}
   */
  public runAsyncCommandLegacy({ hid, params, options }: RunAsyncCommandType): Observable<any> {
    const result = new AsyncProcessSimple<any>(hid, params, options, true);

    return this.runAsync<any>(result);
  }

  private runAsync<TResult>(process: AsyncProcessAbstract<TResult, unknown>): Observable<TResult> {
    const finish = new Subject<void>();
    process.$intermediateResult
      .asObservable()
      .pipe(takeUntil(finish.asObservable()))
      .subscribe((data) => this.$intermediateResult.next(data));

    return this.http.post<TResult>(process.getUrl(true), process.toJSON(true), process.options).pipe(
      switchMap((response) => {
        process.add(response);

        if (!process.canContinue(response)) {
          return of(response);
        }

        return timer(this.interval, this.interval).pipe(
          concatMap(() => this.http.post<TResult>(process.getUrl(), process.toJSON(), process.options)),
          filter((newResponse) => {
            process.add(newResponse);

            return !process.canContinue(newResponse);
          }),
          take(1),
          map((newResponse) => {
            finish.complete();
            process.add(newResponse);

            return process.getResult();
          }),
          catchError((error) => {
            try {
              const result = process.completeResultAfterError();

              return of(result);
            } catch (e) {
              return throwError(error);
            }
          }),
          timeout(this.timeout),
          takeUntil(timer(this.timeout + this.interval))
        );
      })
    );
  }
}

abstract class AsyncProcessAbstract<TResult, TPayload> {
  public options: HttpClientMethodOptions;
  public $intermediateResult: BehaviorSubject<IntermediateAsyncResult> = new BehaviorSubject(null);
  protected minAgentVersion: {
    [AgentType.RA]: number;
    [AgentType.Backup]: number;
    [AgentType.RMM]: number;
    [AgentType.DeepInst]: number;
    [AgentType.NetworkDiscovery]: number;
  } = {
    [AgentType.RA]: 0,
    [AgentType.Backup]: 0,
    [AgentType.RMM]: 0,
    [AgentType.DeepInst]: 0,
    [AgentType.NetworkDiscovery]: 0
  };
  protected payload: TPayload;
  protected resultMap: {
    [AgentType.RA]: Record<string, string | object>;
    [AgentType.Backup]: Record<string, string | object>;
    [AgentType.RMM]: Record<string, string | object>;
    [AgentType.DeepInst]: Record<string, string | object>;
    [AgentType.NetworkDiscovery]: Record<string, string | object>;
  } = {
    [AgentType.RA]: {},
    [AgentType.Backup]: {},
    [AgentType.RMM]: {},
    [AgentType.DeepInst]: {},
    [AgentType.NetworkDiscovery]: {}
  };
  protected response: TResult & {
    isErrorsInResultList?: boolean;
    resultList?: Array<{
      hid: string;
      agentType: AgentType;
      agentVersion: string;
      inProgress: boolean;
      result: { data?: { requestId?: string } };
    }>;
  };
  private id = new Date().getTime();

  protected constructor(
    public url: string,
    payload: TPayload,
    options?: HttpClientMethodOptions
  ) {
    this.payload = payload;
    this.options = options || { headers: { 'Content-Type': 'application/json; charset=utf-8' } };

    this.turnAsync();
  }

  protected legacyMode(commandType: RemoteCommandType): boolean {
    return !AsyncAgentMinVersion.some(
      (data) => data.commandType === commandType && data.agents.some((data) => this.minAgentVersion[data.agentType] >= data.minVersion)
    );
  }

  public canContinue(response: TResult): boolean {
    if ((response as typeof this.response)?.isErrorsInResultList) {
      return false; // TODO: test
    }

    const list = ((response as typeof this.response) || this.response)?.resultList || [];

    return list.some((item) => item.result?.data?.requestId);
  }

  public getResult(): TResult {
    return this.getCurrentResult();
  }

  public getUrl(firstRequest?: boolean): string {
    return this.url;
  }

  protected detectMinAgentVersion(newResponse: TResult): void {
    this.resetMinAgentVersion();

    (newResponse as typeof this.response).resultList
      .filter((item) => item.result?.data?.requestId)
      .forEach((item) => {
        const version = +((item.agentVersion || '').split('.').slice(0, 3).join('') || 0);

        this.minAgentVersion[item.agentType] = Math.min(this.minAgentVersion[item.agentType] || Number.POSITIVE_INFINITY, version);
      });
  }

  protected resetMinAgentVersion(): void {
    Object.keys(this.minAgentVersion).forEach((key: string) => (this.minAgentVersion[key] = 0));
  }

  protected notifyIntermediateResult(): void {
    const result: IntermediateAsyncResult = {
      id: this.id,
      request: { ...this.payload } as Record<string, unknown>,
      response: this.getCurrentResult() as Record<string, unknown>,
      totalComputers: (this.response.resultList || []).length
    };

    this.$intermediateResult.next(result);
  }

  public abstract toJSON(firstRequest?: boolean): string;

  public add(newResponse: TResult): void {
    if (!this.response) {
      // Save first original response as template
      this.response = newResponse as typeof this.response;
    }

    (newResponse as typeof this.response).resultList.forEach((item) => {
      this.resultMap[item.agentType][item.hid] = item;
    });

    this.detectMinAgentVersion(newResponse);
    this.notifyIntermediateResult();
  }

  public completeResultAfterError(): TResult | never {
    if (!this.response) {
      throw new Error();
    }

    const result = this.getCurrentResult();

    return {
      ...result,
      isErrorsInResultList: true,
      ok: false,
      resultList: this.response.resultList.map((item) => {
        let result = this.resultMap[item.agentType][item.hid] || item;

        if (typeof (result as typeof item)?.result?.data?.requestId === 'string') {
          result = { ...item, result: { data: { result: false }, ok: false } };
        }

        (result as typeof item).inProgress = false;

        return result;
      })
    };
  }

  protected abstract turnAsync(): void;

  protected getCurrentResult(): TResult {
    return {
      ...this.response,
      resultList: this.response.resultList.map((item) => {
        const newItem = this.resultMap[item.agentType][item.hid] as typeof item;
        const inProgress = typeof newItem?.result?.data?.requestId === 'string';

        return { ...item, ...newItem, inProgress };
      }) as typeof this.response.resultList
    };
  }
}

class AsyncProcessSimple<TResult> extends AsyncProcessAbstract<TResult, Params> {
  constructor(
    private hid: string,
    payload: Params,
    options: HttpClientMethodOptions,
    private legacy?: boolean
  ) {
    super(getRemoteCommandUrl(hid, payload.commandType), payload, options);
  }

  public getUrl(firstRequest?: boolean): string {
    return getRemoteCommandUrl(this.hid, this.getRequestPayload(firstRequest).commandType);
  }

  public toJSON(firstRequest?: boolean): string {
    return JSON.stringify(this.getRequestPayload(firstRequest));
  }

  public add(newResponse: TResult): void {
    this.response = newResponse;
  }

  public canContinue(response: TResult): boolean {
    if (this.legacy && this.payload.commandType === 'DeleteBackupObject') {
      return false;
    }

    return !!this.getRequestId(response);
  }

  public getResult(): TResult {
    return this.response;
  }

  public completeResultAfterError(): TResult {
    throw new Error('Not implemented');
  }

  protected turnAsync(): void {
    this.payload.params.isAsync = true;
    this.payload.params.timeout = timeoutMilliseconds;
  }

  protected getCurrentResult(): TResult {
    return null;
  }

  protected notifyIntermediateResult() {}

  private getRequestId(response: TResult): string {
    return (response as unknown & { data?: { requestId: string } })?.data?.requestId || null;
  }

  private getRequestPayload(firstRequest?: boolean): Params {
    if (firstRequest) {
      return this.payload;
    }

    if (this.legacy) {
      return { ...this.payload, params: { requestId: this.getRequestId(this.response) } };
    }

    return {
      ...this.payload,
      commandType: RemoteCommandType.GetAsyncResult,
      params: { requestId: this.getRequestId(this.response), isAsync: true, timeoutMilliseconds }
    };
  }
}

class GroupAsyncProcess<TCommands extends AgentRemoteCommand<AgentRemoteCommandType>, TResult> extends AsyncProcessAbstract<
  TResult,
  GroupRemoteCommand<TCommands>
> {
  constructor(url: string, payload: GroupRemoteCommand<TCommands>) {
    super(url, payload);
  }

  public toJSON(firstRequest?: boolean): string {
    if (firstRequest) {
      return JSON.stringify(this.payload);
    }

    const isLegacyMode = this.legacyMode(this.payload.commandType);

    const request = {
      commandType: isLegacyMode ? this.payload.commandType : 'GetAsyncResult',
      agentCommands: this.payload.agentCommands.reduce((result, current) => {
        const data = this.resultMap[current.agentType][current.computerHid];
        const requestId = (data as any)?.result?.data?.requestId;

        if (typeof requestId === 'string') {
          result.push({
            ...current,
            params: {
              requestId,
              isAsync: true,
              ...(isLegacyMode ? { timeout: timeoutMilliseconds } : { timeoutMilliseconds })
            }
          });
        }

        return result;
      }, [])
    };

    return JSON.stringify(request);
  }

  protected turnAsync(): void {
    this.payload.agentCommands.forEach((item) => {
      item['params'] = { ...item['params'], isAsync: true, timeout: timeoutMilliseconds };
    });
  }
}

class BulkAsyncProcess<TParams extends BulkRemoteCommandType, TResult> extends AsyncProcessAbstract<TResult, BulkRemoteCommand<TParams>> {
  constructor(
    url: string,
    private requestIdUrl: string,
    payload: BulkRemoteCommand<TParams>
  ) {
    super(url, payload);
  }

  public getUrl(firstRequest?: boolean): string {
    if (firstRequest) {
      return super.getUrl(firstRequest);
    }

    return this.requestIdUrl;
  }

  public toJSON(firstRequest?: boolean): string {
    if (firstRequest) {
      return JSON.stringify(this.payload);
    }

    const agentCommands = [];
    const resultMapItems = Object.keys(this.resultMap).reduce((result, current) => {
      result = result.concat(
        Object.keys(this.resultMap[current]).reduce((res, cur) => {
          res.push(this.resultMap[current][cur]);

          return res;
        }, [])
      );

      return result;
    }, []) as Array<{ hid: string; agentType: AgentType; result: { data?: { requestId?: string } } }>;

    const isLegacyMode = this.legacyMode(this.payload.commandType);

    resultMapItems.forEach((data) => {
      const requestId = data?.result?.data?.requestId;

      if (typeof requestId === 'string') {
        agentCommands.push({
          computerHid: data.hid,
          agentType: data.agentType,
          params: {
            requestId,
            isAsync: true,
            ...(isLegacyMode ? { timeout: timeoutMilliseconds } : { timeoutMilliseconds })
          }
        });
      }
    });

    const request = {
      commandType: isLegacyMode ? this.payload.commandType : 'GetAsyncResult',
      agentCommands
    };

    return JSON.stringify(request);
  }

  protected turnAsync() {
    this.payload.params = { ...this.payload.params, isAsync: true, timeout: timeoutMilliseconds };
  }
}
